import { useQuery } from '@tanstack/react-query';
import type { LeadFilterState, LeadResultsResponse } from '../types/leads';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Build API query string from filter state
 */
function buildApiQueryString(filters: LeadFilterState): string {
  const params = new URLSearchParams();

  // Tiers
  if (filters.tiers.length > 0) {
    params.set('tiers', filters.tiers.join(','));
  }

  // ZIP codes
  if (filters.zipCodes.length > 0) {
    params.set('zip_codes', filters.zipCodes.join(','));
  }

  // Unit range
  if (filters.unitMin !== null) {
    params.set('unit_min', String(filters.unitMin));
  }
  if (filters.unitMax !== null) {
    params.set('unit_max', String(filters.unitMax));
  }

  // Score range
  if (filters.scoreMin !== null) {
    params.set('score_min', String(filters.scoreMin));
  }
  if (filters.scoreMax !== null) {
    params.set('score_max', String(filters.scoreMax));
  }

  // License status
  if (filters.licenseStatus !== 'any') {
    params.set('license_status', filters.licenseStatus);
  }

  // Market status
  if (filters.marketStatus !== 'any') {
    params.set('market_status', filters.marketStatus);
  }

  // Date range
  if (filters.dateFrom) {
    params.set('date_from', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set('date_to', filters.dateTo);
  }

  // Sorting
  params.set('sort_by', filters.sortBy);
  params.set('sort_order', filters.sortOrder);

  // Pagination
  params.set('page', String(filters.page));
  params.set('limit', String(filters.limit));

  // Include filter counts for the panel
  params.set('include_counts', 'true');

  return params.toString();
}

/**
 * Fetch leads from API
 */
async function fetchLeads(queryString: string): Promise<LeadResultsResponse> {
  const response = await fetch(`${API_BASE}/api/leads?${queryString}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Hook for fetching lead results with React Query
 *
 * Accepts filters from useLeadFilters and returns paginated lead results
 * with loading, error, and refetch capabilities.
 */
export function useLeadResults(filters: LeadFilterState) {
  const queryString = buildApiQueryString(filters);

  const query = useQuery<LeadResultsResponse, Error>({
    queryKey: ['leads', queryString],
    queryFn: () => fetchLeads(queryString),
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  return {
    leads: query.data?.leads ?? [],
    total: query.data?.total ?? 0,
    page: query.data?.page ?? filters.page,
    pages: query.data?.pages ?? 0,
    filterCounts: query.data?.filter_counts,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export type UseLeadResultsReturn = ReturnType<typeof useLeadResults>;
